import { useEffect, useState } from 'react';
import {
  useAction,
  useBaseUrlStore,
  useFilterSettingsStore,
  useLabels,
  useLanguageStore,
  useProjectsOverviewStore,
  useWindowWidthStore,
} from 'src/StoreDataProvider';
import ProjectListItem from '../ProjectListItem';
import { ProjectListProps } from './projectList.types';

const ProjectList: (props: ProjectListProps) => JSX.Element = () => {
  const { projectList, numberOfItems, isError } = useProjectsOverviewStore();
  const filterSettings = useFilterSettingsStore();
  const { getProjects, getReportStartYearAndEndYear } = useAction();
  const labels = useLabels();
  const windowState = useWindowWidthStore();
  const { language, firstLanguageChangeDone } = useLanguageStore();
  const [lastFilterSettings, setLastFilterSettings] = useState(filterSettings);
  const { isYearView } = useBaseUrlStore();

  const getFiltersChangedExceptDropdowns = (): boolean => {
    return (
      lastFilterSettings.sort !== filterSettings.sort ||
      lastFilterSettings.page !== filterSettings.page ||
      lastFilterSettings.searchTerm !== filterSettings.searchTerm ||
      lastFilterSettings.size !== filterSettings.size
    );
  };

  const setNewScrollValues = (): void => {
    const scrollElement = document.getElementById('dropdownsPagination');
    const headerMargin = document.getElementById('headerContent')
      ? document.getElementById('headerContent')?.clientHeight
      : 0;
    const headerMarginApplied = headerMargin ?? 0;
    const scrollValue = scrollElement
      ? scrollElement.getBoundingClientRect().top -
        headerMarginApplied +
        document.documentElement.scrollTop
      : 0;
    window.scrollTo({
      top: scrollValue,
    });
  };

  useEffect(() => {
    const tempFilterSettings = { ...filterSettings };
    tempFilterSettings.page = lastFilterSettings.page;
    if (
      filterSettings.page !== lastFilterSettings.page &&
      JSON.stringify(tempFilterSettings) === JSON.stringify(lastFilterSettings)
    ) {
      setNewScrollValues();
    }
  }, [filterSettings.page]);

  useEffect(() => {
    // Shouldnt be fetched when we are in the mobile view
    // Except the filters except the dropdowns changed
    if (!windowState.isMobileView || getFiltersChangedExceptDropdowns()) {
      // Only reload when the settings have changed
      // Or when we are not on page 0
      if (
        JSON.stringify(lastFilterSettings) !== JSON.stringify(filterSettings) ||
        filterSettings.page > 0
      ) {
        setLastFilterSettings(filterSettings);
        getProjects(filterSettings, language, getReportStartYearAndEndYear());
      }
    }
  }, [filterSettings, windowState.isMobileView]);

  useEffect(() => {
    if (firstLanguageChangeDone) {
      getProjects(filterSettings, language, getReportStartYearAndEndYear());
    }
  }, [language, firstLanguageChangeDone, isYearView]);

  return numberOfItems > 0 ? (
    <div className="detail-content">
      {projectList.map(project => {
        return (
          <ProjectListItem key={project.id} {...project}></ProjectListItem>
        );
      })}
    </div>
  ) : isError ? (
    <div className="project-tile-error">
      <h3> {labels.projectLoadingFailure} </h3>
    </div>
  ) : (
    <p id="no-results">
      <strong role="status">
        {filterSettings.searchTerm === ''
          ? labels.noResults
          : `${labels.noResults1} `}
        <span
          style={{
            visibility: filterSettings.searchTerm === '' ? 'hidden' : 'visible',
          }}
        >
          {filterSettings.searchTerm}
        </span>
        {filterSettings.searchTerm !== '' && ` ${labels.noResults2}`}
      </strong>
    </p>
  );
};

export default ProjectList;
